import { useState, useEffect, useCallback } from 'react';
import supabase from '../supabase/supabase';

export const PARTNERSHIP_TYPES = [
  'partnership_request', 'partnership_accepted', 'partnership_declined',
  'partnership_ended', 'partnership_terms_updated', 'milestone_completed',
  'milestone_overdue', 'milestone_progress', 'milestone_status_change',
  'partnership_milestone_added', 'partnership_digest',
];

export const MILESTONE_TYPES = [
  'milestone', 'milestone_completed', 'milestone_overdue',
  'milestone_progress', 'milestone_status_change', 'partnership_milestone_added',
];

export const useNotifications = (userId) => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount]     = useState(0);
  const [loading, setLoading]             = useState(false);

  const fetchNotifications = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    const { data } = await supabase
      .from('d2f_notifications')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
      .limit(80);
    const list = data || [];
    setNotifications(list);
    setUnreadCount(list.filter(n => !n.is_read).length);
    setLoading(false);
  }, [userId]);

  useEffect(() => {
    fetchNotifications();
    if (!userId) return;
    const channel = supabase
      .channel(`notifications:${userId}`)
      .on('postgres_changes', {
        event: 'INSERT', schema: 'public',
        table: 'd2f_notifications',
        filter: `user_id=eq.${userId}`,
      }, (payload) => {
        setNotifications(prev => [payload.new, ...prev]);
        setUnreadCount(c => c + 1);
      })
      .on('postgres_changes', {
        event: 'UPDATE', schema: 'public',
        table: 'd2f_notifications',
        filter: `user_id=eq.${userId}`,
      }, (payload) => {
        setNotifications(prev =>
          prev.map(n => n.id === payload.new.id ? { ...n, ...payload.new } : n)
        );
      })
      .subscribe();
    return () => supabase.removeChannel(channel);
  }, [userId, fetchNotifications]);

  const markRead = async (id) => {
    await supabase.from('d2f_notifications').update({ is_read: true }).eq('id', id);
    setNotifications(prev => prev.map(n => n.id === id ? { ...n, is_read: true } : n));
    setUnreadCount(c => Math.max(0, c - 1));
  };

  const markAllRead = async () => {
    if (!userId) return;
    await supabase.from('d2f_notifications')
      .update({ is_read: true })
      .eq('user_id', userId).eq('is_read', false);
    setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
    setUnreadCount(0);
  };

  const markCategoryRead = async (category) => {
    if (!userId) return;
    await supabase.from('d2f_notifications')
      .update({ is_read: true })
      .eq('user_id', userId).eq('category', category).eq('is_read', false);
    setNotifications(prev =>
      prev.map(n => n.category === category ? { ...n, is_read: true } : n)
    );
    setUnreadCount(prev =>
      notifications.filter(n => n.category !== category && !n.is_read).length
    );
  };

  const deleteNotification = async (id) => {
    await supabase.from('d2f_notifications').delete().eq('id', id);
    setNotifications(prev => prev.filter(n => n.id !== id));
  };

  const addNotification = async ({ type, title, message, contentId, artistId, metadata = {}, priority = 'normal', category = 'general', actionUrl = '', expiresAt = null }) => {
    if (!userId) return;
    const { data } = await supabase.from('d2f_notifications').insert({
      user_id: userId, type, title, message,
      content_id: contentId || null,
      artist_id: artistId || null,
      metadata, priority, category,
      action_url: actionUrl,
      expires_at: expiresAt,
    }).select().single();
    return data;
  };

  // Grouped counts
  const partnershipUnread = notifications.filter(n => !n.is_read && PARTNERSHIP_TYPES.includes(n.type)).length;
  const milestoneUnread   = notifications.filter(n => !n.is_read && MILESTONE_TYPES.includes(n.type)).length;
  const urgentUnread      = notifications.filter(n => !n.is_read && n.priority === 'urgent').length;

  return {
    notifications, unreadCount, loading,
    partnershipUnread, milestoneUnread, urgentUnread,
    markRead, markAllRead, markCategoryRead, deleteNotification,
    addNotification, refetch: fetchNotifications,
  };
};